import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import camelCase from "lodash/camelCase";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import moment from "moment";
import { OrderedMap } from "immutable";
import styled from "styled-components";
import { describeExpression } from "util/CronUtils";
import { isPermitted } from "util/PermissionsMixin";
import * as FormsUtils from "util/FormsUtils";
import FormWarningsContext from "contexts/FormWarningsContext";
import Store from "logic/local-storage/Store";
import { TimeUnitInput, SearchFiltersFormControls, TimezoneSelect } from "components/common";
import Query from "views/logic/queries/Query";
import Search from "views/logic/search/Search";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { Alert, ButtonToolbar, ControlLabel, FormGroup, HelpBlock, Input } from "components/bootstrap";
import usePluginEntities from "hooks/usePluginEntities";
import RelativeTime from "components/common/RelativeTime";
import Parameter from "views/logic/parameters/Parameter";
import validateQuery from "views/components/searchbar/queryvalidation/validateQuery";
import generateId from "logic/generateId";
import parseSearch from "views/logic/slices/parseSearch";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useUserDateTime from "hooks/useUserDateTime";
import { indicesInWarmTier, isSearchingWarmTier } from "views/components/searchbar/queryvalidation/warmTierValidation";
import StreamsAndCategoriesFilter from "views/components/common/StreamsAndCategoriesFilter";
import ViewsQueryInput from "views/components/searchbar/ViewsQueryInput";
import QueryValidationDisplay from "views/components/searchbar/queryvalidation/QueryValidationDisplay";
import useScopePermissions from "hooks/useScopePermissions";
import EditQueryParameterModal from "../event-definition-form/EditQueryParameterModal";
import commonStyles from "../common/commonStyles.css";
export const PLUGGABLE_CONTROLS_HIDDEN_KEY = "pluggableSearchBarControlsAreHidden";
export const TIME_UNITS = ["HOURS", "MINUTES", "SECONDS"];
const LOOKUP_PERMISSIONS = ["lookuptables:read"];
const STREAM_PERMISSIONS = ["streams:read"];
const InputRow = styled.div`
  display: flex;
  align-items: center;
`;
const buildNewParameter = (name) => ({
  name,
  embryonic: true,
  type: "lut-parameter-v1",
  data_type: "any",
  title: "new title"
});
const toTimeRange = (from) => ({
  type: "relative",
  from: from / 1e3
});
const WarmTierTimeStamp = () => {
  const { warnings } = useContext(FormWarningsContext);
  const validationState = warnings == null ? void 0 : warnings.queryString;
  const warmTierRanges = indicesInWarmTier(validationState);
  const latestWarmTierRangeEnd = warmTierRanges.map((range) => range.end).sort((a, b) => b - a)[0];
  return /* @__PURE__ */ React.createElement(RelativeTime, { dateTime: latestWarmTierRangeEnd });
};
const QueryParameters = ({ eventDefinition, onChange, validation, userCanViewLookupTables }) => {
  var _a, _b;
  const queryParameters = (_b = (_a = eventDefinition == null ? void 0 : eventDefinition.config) == null ? void 0 : _a.query_parameters) != null ? _b : [];
  const onChangeQueryParameters = useCallback(
    (newQueryParameters) => {
      const newConfig = { ...eventDefinition.config, query_parameters: newQueryParameters || [] };
      return onChange(newConfig);
    },
    [eventDefinition.config, onChange]
  );
  if (!userCanViewLookupTables) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "This account lacks permission to declare Query Parameters from Lookup Tables.");
  }
  const parameterButtons = queryParameters.map((queryParam) => {
    let parsed;
    try {
      parsed = Parameter.fromJSON(queryParam);
    } catch {
      return null;
    }
    return /* @__PURE__ */ React.createElement(
      EditQueryParameterModal,
      {
        key: queryParam.name,
        queryParameter: parsed,
        embryonic: !!queryParam.embryonic,
        queryParameters,
        onChange: onChangeQueryParameters
      }
    );
  });
  if (isEmpty(parameterButtons)) {
    return null;
  }
  const hasEmbryonicParameters = !isEmpty(
    queryParameters.filter((param) => param.embryonic)
  );
  return /* @__PURE__ */ React.createElement(FormGroup, { validationState: validation.errors.query_parameters ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Query Parameters"), /* @__PURE__ */ React.createElement(Alert, { bsStyle: hasEmbryonicParameters ? "danger" : "info" }, /* @__PURE__ */ React.createElement(ButtonToolbar, null, parameterButtons)), hasEmbryonicParameters && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.query_parameters ? validation == null ? void 0 : validation.errors.query_parameters[0] : "Please declare missing query parameters by clicking on the buttons above."));
};
const FilterForm = ({ currentUser, eventDefinition, onChange, streams, validation }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
  const { execute_every_ms: executeEveryMs, search_within_ms: searchWithinMs } = eventDefinition.config;
  const [currentConfig, setCurrentConfig] = useState(eventDefinition.config);
  const searchQueryPreviews = (_a = usePluginEntities("eventDefinitions.components.searchQueryPreview")) != null ? _a : [];
  const searchWithin = extractDurationAndUnit(searchWithinMs, TIME_UNITS);
  const executeEvery = extractDurationAndUnit(executeEveryMs, TIME_UNITS);
  const { userTimezone } = useUserDateTime();
  const { setFieldWarning, warnings } = useContext(FormWarningsContext);
  const validationState = warnings == null ? void 0 : warnings.queryString;
  const warmTierRanges = indicesInWarmTier(validationState);
  const sendTelemetry = useSendTelemetry("event-definition-condition");
  const queryId = generateId();
  const searchTypeId = generateId();
  const [queryParameterStash, setQueryParameterStash] = useState({});
  const [searchFiltersHidden, setSearchFiltersHidden] = useState(false);
  const [searchWithinMsUnit, setSearchWithinMsUnit] = useState(searchWithin.unit);
  const [executeEveryMsUnit, setExecuteEveryMsUnit] = useState(executeEvery.unit);
  const [searchWithinMsDuration, setSearchWithinMsDuration] = useState(searchWithin.duration);
  const [executeEveryMsDuration, setExecuteEveryMsDuration] = useState(executeEvery.duration);
  const userCanViewLookupTables = useMemo(
    () => isPermitted(currentUser.permissions, LOOKUP_PERMISSIONS),
    [currentUser.permissions]
  );
  const isStreamRequired = useMemo(
    () => !isPermitted(currentUser.permissions, STREAM_PERMISSIONS),
    [currentUser.permissions]
  );
  const [cronDescription, setCronDescription] = useState(
    currentConfig.cron_expression ? describeExpression(currentConfig.cron_expression) : ""
  );
  const { scopePermissions } = useScopePermissions(eventDefinition);
  const isMutable = scopePermissions == null ? void 0 : scopePermissions.is_mutable;
  const validateQueryString = useCallback(
    (queryString, streamIds, timeRange, timezone) => {
      const request = {
        timeRange,
        queryString,
        streams: streamIds
      };
      validateQuery(request, timezone).then((result) => {
        if ((result == null ? void 0 : result.status) === "WARNING" || (result == null ? void 0 : result.status) === "ERROR") {
          setFieldWarning("queryString", result);
        } else {
          setFieldWarning("queryString", void 0);
        }
      });
    },
    [setFieldWarning]
  );
  useEffect(() => {
    validateQueryString(
      eventDefinition.config.query,
      eventDefinition.config.streams,
      toTimeRange(eventDefinition.config.search_within_ms),
      userTimezone
    );
  }, [
    eventDefinition.config.query,
    eventDefinition.config.streams,
    eventDefinition.config.search_within_ms,
    setFieldWarning,
    userTimezone,
    validateQueryString
  ]);
  const getUpdatedConfig = useCallback(
    (key, value) => {
      const config = { ...eventDefinition.config, [key]: value };
      setCurrentConfig(config);
      return config;
    },
    [eventDefinition.config]
  );
  const getUpdatedConfigMulti = useCallback(
    (updates) => {
      const config = {
        ...eventDefinition.config,
        ...Object.fromEntries(updates.map(({ key, value }) => [key, value]))
      };
      setCurrentConfig(config);
      return config;
    },
    [eventDefinition.config]
  );
  const propagateChange = useCallback(
    (config) => {
      onChange("config", config);
    },
    [onChange]
  );
  const syncParamsWithQuery = useCallback(
    (paramsInQuery, config) => {
      const queryParameters = (config == null ? void 0 : config.query_parameters) || [];
      const keptParameters = [];
      const staleParameters = {};
      queryParameters.forEach((p) => {
        if (paramsInQuery.has(p.name)) {
          keptParameters.push(p);
        } else {
          staleParameters[p.name] = p;
        }
      });
      const newParameters = [];
      paramsInQuery.forEach((np) => {
        if (!keptParameters.find((p) => p.name === np)) {
          if (queryParameterStash[np]) {
            newParameters.push(queryParameterStash[np]);
          } else {
            newParameters.push(buildNewParameter(np));
          }
        }
      });
      setQueryParameterStash(merge(queryParameterStash, staleParameters));
      propagateChange({ ...config, query_parameters: keptParameters.concat(newParameters) });
    },
    [propagateChange, queryParameterStash]
  );
  const parseQuery = useCallback(
    (queryString, config, searchFilters = OrderedMap()) => {
      if (!userCanViewLookupTables) {
        return;
      }
      const queryBuilder = Query.builder().id(queryId).query({ type: "elasticsearch", query_string: queryString }).timerange({ type: "relative", range: 1e3 }).filters(searchFilters.toList()).searchTypes([
        {
          id: searchTypeId,
          type: "messages",
          limit: 10,
          offset: 0,
          filter: void 0,
          filters: void 0,
          name: void 0,
          query: void 0,
          timerange: void 0,
          streams: void 0,
          stream_categories: void 0,
          sort: [],
          decorators: []
        }
      ]);
      const query = queryBuilder.build();
      const search = Search.create().toBuilder().queries([query]).build();
      parseSearch(search).then((res) => {
        syncParamsWithQuery(res.undeclared, config);
      });
    },
    [queryId, searchTypeId, syncParamsWithQuery, userCanViewLookupTables]
  );
  const debouncedParseQuery = debounce(parseQuery, 250);
  const handleConfigChange = useCallback(
    (name, config) => {
      if (name === "_is_scheduled") {
        sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.FILTER_EXECUTED_AUTOMATICALLY_TOGGLED, {
          app_action_value: "enable-checkbox",
          is_scheduled: config._is_scheduled
        });
      }
      propagateChange(config);
    },
    [propagateChange, sendTelemetry]
  );
  const handleQueryChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      const newConfig = getUpdatedConfig(name, value);
      handleConfigChange(name, newConfig);
      debouncedParseQuery(value, newConfig);
      return Promise.resolve(value);
    },
    [debouncedParseQuery, getUpdatedConfig, handleConfigChange]
  );
  const handleCronExpressionChange = useCallback(
    (event) => {
      const { name } = event.target;
      const value = FormsUtils.getValueFromInput(event.target);
      const newConfig = getUpdatedConfig(name, value);
      handleConfigChange(name, newConfig);
    },
    [getUpdatedConfig, handleConfigChange]
  );
  const handleCronTimezoneChange = useCallback(
    (tz) => {
      const newConfig = getUpdatedConfig("cron_timezone", tz);
      handleConfigChange("cron_timezone", newConfig);
    },
    [getUpdatedConfig, handleConfigChange]
  );
  const handleSearchFiltersChange = useCallback(
    (searchFilters) => {
      const { query } = eventDefinition.config;
      const newConfig = getUpdatedConfig("filters", searchFilters.toArray());
      propagateChange(newConfig);
      debouncedParseQuery(query, newConfig);
    },
    [debouncedParseQuery, eventDefinition.config, getUpdatedConfig, propagateChange]
  );
  const handleEnabledChange = useCallback(
    (event) => {
      const { name } = event.target;
      const value = FormsUtils.getValueFromInput(event.target);
      const newConfig = getUpdatedConfig(name, value);
      handleConfigChange(name, newConfig);
    },
    [getUpdatedConfig, handleConfigChange]
  );
  const handleUseCronSchedulingChange = useCallback(
    (event) => {
      const { name } = event.target;
      const value = FormsUtils.getValueFromInput(event.target);
      const newConfig = {
        ...eventDefinition.config,
        [name]: value,
        cron_expression: value ? "" : null,
        cron_timezone: value ? userTimezone : null
      };
      setCurrentConfig(newConfig);
      propagateChange(newConfig);
    },
    [eventDefinition.config, propagateChange, userTimezone]
  );
  const hideFiltersPreview = useCallback((value) => {
    Store.set(PLUGGABLE_CONTROLS_HIDDEN_KEY, value);
    setSearchFiltersHidden(value);
  }, []);
  const handleStreamsAndCategoriesChange = useCallback(
    (selected) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.FILTER_STREAM_SELECTED, {
        app_action_value: "stream-select"
      });
      propagateChange(
        getUpdatedConfigMulti([
          { key: "streams", value: selected.streams },
          { key: "stream_categories", value: selected.categories }
        ])
      );
    },
    [getUpdatedConfigMulti, propagateChange, sendTelemetry]
  );
  const handleTimeRangeChange = useCallback(
    (fieldName) => (nextValue, nextUnit) => {
      if (fieldName === "search_within_ms" && nextUnit !== searchWithinMsUnit) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.FILTER_SEARCH_WITHIN_THE_LAST_UNIT_CHANGED, {
          app_action_value: "searchWithinMsUnit-select",
          new_unit: nextUnit
        });
      } else if (fieldName === "execute_every_ms" && nextUnit !== executeEveryMsUnit) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.FILTER_EXECUTE_SEARCH_EVERY_UNIT_CHANGED, {
          app_action_value: "executeEveryMsUnit-select",
          new_unit: nextUnit
        });
      }
      const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
      propagateChange(getUpdatedConfig(fieldName, durationInMs));
      const stateFieldName = camelCase(fieldName);
      if (stateFieldName === "searchWithinMs") {
        setSearchWithinMsDuration(nextValue);
        setSearchWithinMsUnit(nextUnit);
        return;
      }
      setExecuteEveryMsDuration(nextValue);
      setExecuteEveryMsUnit(nextUnit);
    },
    [executeEveryMsUnit, getUpdatedConfig, propagateChange, searchWithinMsUnit, sendTelemetry]
  );
  const onlyFilters = eventDefinition._scope === "ILLUMINATE";
  const currentSelection = {
    streams: (_c = (_b = eventDefinition.config) == null ? void 0 : _b.streams) != null ? _c : [],
    categories: (_e = (_d = eventDefinition.config) == null ? void 0 : _d.stream_categories) != null ? _e : []
  };
  const streamsAndCategories = [
    .../* @__PURE__ */ new Set([...streams.map((s) => s.id), ...(_g = (_f = eventDefinition == null ? void 0 : eventDefinition.config) == null ? void 0 : _f.streams) != null ? _g : []])
  ].map((streamId) => {
    var _a2;
    const stream = streams.find((s) => s.id === streamId);
    return { title: (_a2 = stream == null ? void 0 : stream.title) != null ? _a2 : streamId, id: streamId, categories: stream == null ? void 0 : stream.categories };
  });
  return /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, "Filter"), /* @__PURE__ */ React.createElement("p", null, "Add information to filter the log messages that are relevant for this Event Definition."), onlyFilters || /* @__PURE__ */ React.createElement(FormGroup, { controlId: "query-string" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Search Query"), /* @__PURE__ */ React.createElement(InputRow, null, /* @__PURE__ */ React.createElement(
    ViewsQueryInput,
    {
      inputId: "filter-query",
      name: "query",
      value: (_h = currentConfig.query) != null ? _h : "",
      onChange: handleQueryChange,
      isValidating: false,
      validate: () => Promise.resolve({}),
      onExecute: () => {
      },
      streams: eventDefinition.config.streams,
      timeRange: toTimeRange(eventDefinition.config.search_within_ms),
      error: (validationState == null ? void 0 : validationState.status) === "ERROR" ? validationState : void 0,
      warning: (validationState == null ? void 0 : validationState.status) === "WARNING" ? validationState : void 0
    }
  ), /* @__PURE__ */ React.createElement(QueryValidationDisplay, null)), /* @__PURE__ */ React.createElement(HelpBlock, null, "Search query that Messages should match. You can use the same syntax as in the Search page, including declaring Query Parameters from Lookup Tables by using the ", /* @__PURE__ */ React.createElement("code", null, "$newParameter$"), " syntax.")), onlyFilters || searchQueryPreviews.map(({ component: SearchQueryPreview, key }) => {
    var _a2, _b2;
    return /* @__PURE__ */ React.createElement(SearchQueryPreview, { key, queryString: (_a2 = currentConfig.query) != null ? _a2 : "", filters: (_b2 = currentConfig.filters) != null ? _b2 : [] });
  }), onlyFilters || /* @__PURE__ */ React.createElement(
    QueryParameters,
    {
      eventDefinition,
      onChange: propagateChange,
      validation,
      userCanViewLookupTables
    }
  ), !searchFiltersHidden && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-filters" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Search Filters ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement("div", { style: { margin: "16px 0" } }, /* @__PURE__ */ React.createElement(
    SearchFiltersFormControls,
    {
      filters: eventDefinition.config.filters,
      onChange: handleSearchFiltersChange,
      hideFiltersPreview,
      queryString: currentConfig.query,
      isParentMutable: isMutable
    }
  ))), onlyFilters || /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "filter-streams-and-categories" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Streams and Categories ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, isStreamRequired ? "(Optional)" : "")), /* @__PURE__ */ React.createElement(
    StreamsAndCategoriesFilter,
    {
      id: "filter-streams-and-categories",
      required: isStreamRequired,
      onChange: handleStreamsAndCategoriesChange,
      value: currentSelection,
      streams: streamsAndCategories
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Select streams the search should include.")), isSearchingWarmTier(warmTierRanges) && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: "Warm Tier Warning" }, "The selected time range will include data stored in the Warm Tier. Events that must frequently retrieve data from the Warm Tier may cause performance problems. A value for", " ", /* @__PURE__ */ React.createElement("strong", null, "Search within the last"), " exceeding the following duration will fall into the Warm Tier:", " ", /* @__PURE__ */ React.createElement(WarmTierTimeStamp, null), "."), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-within", validationState: validation.errors.search_within_ms ? "error" : null }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "Search within the last",
      update: handleTimeRangeChange("search_within_ms"),
      value: searchWithinMsDuration,
      unit: searchWithinMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), validation.errors.search_within_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.search_within_ms[0])), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "is-cron-checkbox",
      type: "checkbox",
      name: "use_cron_scheduling",
      label: "Use Cron Scheduling",
      help: "Schedule this event with a Quartz cron expression",
      checked: (_i = eventDefinition.config.use_cron_scheduling) != null ? _i : false,
      onChange: handleUseCronSchedulingChange
    }
  ), currentConfig.use_cron_scheduling ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormGroup,
    {
      controlId: "cron-expression",
      validationState: validation.errors.cron_expression ? "error" : null
    },
    /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "cron-expression",
        name: "cron_expression",
        label: "Cron Expression",
        type: "text",
        help: /* @__PURE__ */ React.createElement("span", null, cronDescription || "A Quartz cron expression to determine when the event should be run."),
        value: (_j = currentConfig.cron_expression) != null ? _j : "",
        onBlur: () => setCronDescription(describeExpression(currentConfig.cron_expression)),
        onChange: handleCronExpressionChange
      }
    ),
    validation.errors.cron_expression && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.cron_expression[0])
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Cron Time Zone"), /* @__PURE__ */ React.createElement(
    TimezoneSelect,
    {
      value: (_k = currentConfig.cron_timezone) != null ? _k : userTimezone,
      name: "cron_timezone",
      clearable: false,
      onChange: handleCronTimezoneChange
    }
  ))) : /* @__PURE__ */ React.createElement(FormGroup, { controlId: "execute-every", validationState: validation.errors.execute_every_ms ? "error" : null }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "Execute search every",
      update: handleTimeRangeChange("execute_every_ms"),
      value: executeEveryMsDuration,
      unit: executeEveryMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), validation.errors.execute_every_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.execute_every_ms[0])), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "schedule-checkbox",
      type: "checkbox",
      name: "_is_scheduled",
      label: "Enable",
      help: "Should this event definition be executed automatically?",
      checked: (_l = eventDefinition.config._is_scheduled) != null ? _l : true,
      onChange: handleEnabledChange
    }
  )));
};
export default FilterForm;
